import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import useSWR from 'swr';

import { doRequest } from '@r1-frontend/do-request';
import { IPromoline } from '@r1-frontend/api-domru/api-content/v1/geography/get-city-info/dto/IPromoline';
import { promoLine } from '@r1-frontend/api-domru/api-content/v1/promo-line';

import type { TState } from '~/src/store';
import { selectHasAuthorization } from '~/src/store/selectors/auth/selectAccessToken';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const PATHS_CONTAIN_TO_HIDE_LINE: string[] = [
    'payments',
    'full-buy',
    'news',
    'service',
];

interface IPromolinesData {
    data: IPromoline[],
    status: 'initialised' | 'loaded',
}

export interface IUseHatResponse {
    promoline?: IPromoline,
    isShowPromoLine: boolean,
}

export const useHat = (): IUseHatResponse => {
    const [promolines, setPromolines] = useState<IPromolinesData>({
        data: [],
        status: 'initialised',
    });

    const { withPromoLines, hasAuthorization, providerId } = useSelector((state: TState) => ({
        withPromoLines: state.city.currentCity.withPromoLines,
        hasAuthorization: selectHasAuthorization(state),
        providerId: selectProviderId(state),
    }));

    const hasPromoLine = useMemo(() => hasAuthorization
            ? withPromoLines?.auth
            : withPromoLines?.unauth,
        [hasAuthorization, withPromoLines]);

    const { data: cachedPromolines } = useSWR(
        hasPromoLine ? ['promolines', providerId] : null,
        async() => {
            const resp = await doRequest.apiContent<{ items: IPromoline[] }>(promoLine(providerId));
            return resp.isSuccess ? resp.payload.items : [];
        },
        { revalidateOnFocus: false, dedupingInterval: 60000 },
    );

    useEffect(() => {
        if (hasPromoLine && promolines.status === 'initialised' && cachedPromolines) {
            setPromolines({ data: cachedPromolines, status: 'loaded' });
        }
    }, [hasPromoLine, providerId, promolines, cachedPromolines]);

    const { asPath } = useRouter() || { asPath: '' };

    const promoline = useMemo(
        () =>
            promolines.data?.find((line) => {
                if (line.is_active) {
                    return hasAuthorization ? line.is_view_auth : line.is_view_guest;
                }
                return false;
            }),
        [hasAuthorization, promolines.data],
    );

    const hideLineByPath = useMemo(() => {
        const paths = decodeURIComponent(asPath).split(/[?///&/=]/);
        return PATHS_CONTAIN_TO_HIDE_LINE.some(link => paths.includes(link));
    }, [asPath]);

    return {
        promoline,
        isShowPromoLine: (!hideLineByPath && !!hasPromoLine) &&
            ((promolines.status === 'loaded' && !!promoline) || promolines.status !== 'loaded'),
    };
};
